import { MenuItem } from 'primeng/api';
import { RoleType } from '@/core/admin/constants/role.constants';
import { AppGlyphKey } from '@/shared/components/app-glyph/app-glyph.component';
import { APP_REGISTRY, AppDefinition } from './app-registry';
import { matchesPathPrefix, stripQueryAndFragment } from './active-section';
import { isMenuItemVisible, MenuGate, MenuVisibilityContext } from './menu-visibility';

/**
 * Sidebar menu model (SCRUM-594, masthead epic story 7). Pure: no DI, no
 * router, so the derivation and the gating can be specced without the store.
 *
 * The main menu is derived from the app registry: an "Apps" header with one
 * row per app (glyph + name, no sub-items) and a "System" header with the
 * Administration link, which carries the `admin` glyph (the gear) so it opens
 * and closes like the app rows (see the SCRUM-607 rule in
 * `_layout-overrides.scss`). The admin menu (`/admin/*`) is derived from the
 * admin registry the same way in `admin-menu.model.ts` (SCRUM-622).
 *
 * `AppMenu.updateMenuVisibility()` deep-clones the model through JSON before
 * filtering, so everything here must be plain data: `glyph` is a string key
 * (never a function or a component) and `activeWhen` a string array.
 */

/** A sidebar entry: PrimeNG `MenuItem` plus Envolve's gating and rendering fields. */
export interface AppMenuItem extends MenuItem, MenuGate {
  /** App glyph drawn by `app.menuitem.ts` instead of the PrimeIcons `icon`. */
  glyph?: AppGlyphKey;
  /**
   * URL path prefixes (no query/fragment) that keep this item highlighted,
   * e.g. the app's or admin group's `activeWhen`. Absent = exact `routerLink`
   * match (the Ultima default, still used by the admin menu's back link).
   */
  activeWhen?: readonly string[];
  items?: AppMenuItem[];
}

/** Roles that can open the Administration area (System header and its link). */
export const ADMINISTRATION_ROLES: readonly RoleType[] = [
  RoleType.SYSTEM_ADMIN,
  RoleType.ORGANIZATION_ADMIN,
  RoleType.TEAM_LEAD,
];

/**
 * Builds the main (non-admin) sidebar model from the registry, in registry
 * order. Gating fields are copied from each app so `filterMenuTree` and the
 * masthead agree on who sees what.
 */
export function buildMainMenuModel(registry: readonly AppDefinition[] = APP_REGISTRY): AppMenuItem[] {
  return [
    {
      label: 'Apps',
      icon: 'pi pi-home',
      items: registry.map(toAppMenuItem),
    },
    {
      label: 'System',
      icon: 'pi pi-cog',
      requiredRoles: [...ADMINISTRATION_ROLES],
      items: [
        {
          label: 'Administration',
          glyph: 'admin',
          routerLink: ['/admin'],
          requiredRoles: [...ADMINISTRATION_ROLES],
        },
      ],
    },
  ];
}

function toAppMenuItem(app: AppDefinition): AppMenuItem {
  const item: AppMenuItem = {
    label: app.name,
    glyph: app.glyph,
    routerLink: [app.home],
    activeWhen: [...app.activeWhen],
  };
  // Only copy the gates the app declares, so the model stays as lean as the
  // one it replaced (an undefined key would be dropped by the JSON clone anyway).
  if (app.requiredRoles) {
    item.requiredRoles = [...app.requiredRoles];
  }
  if (app.requireAllRoles !== undefined) {
    item.requireAllRoles = app.requireAllRoles;
  }
  if (app.requiresWmEnabledTeam !== undefined) {
    item.requiresWmEnabledTeam = app.requiresWmEnabledTeam;
  }
  return item;
}

/**
 * Drops the items the user may not see (per `isMenuItemVisible`) and any
 * parent left with no visible children and no link of its own. Returns new
 * parent objects; leaves are passed through untouched.
 */
export function filterMenuTree(items: readonly AppMenuItem[], ctx: MenuVisibilityContext): AppMenuItem[] {
  const visible: AppMenuItem[] = [];
  for (const item of items) {
    if (!isMenuItemVisible(item, ctx)) {
      continue;
    }
    if (!item.items) {
      visible.push(item);
      continue;
    }
    const children = filterMenuTree(item.items, ctx);
    if (children.length === 0 && !item.routerLink) {
      continue;
    }
    visible.push({ ...item, items: children });
  }
  return visible;
}

/**
 * Prefix-based active state: true when the URL's path (query and fragment
 * ignored) is one of the item's `activeWhen` prefixes or a whole-segment
 * descendant of one. Items without `activeWhen` are never active here; they
 * rely on `routerLinkActive` exact matching instead.
 */
export function isMenuItemActive(item: Pick<AppMenuItem, 'activeWhen'> | null | undefined, url: string): boolean {
  if (!item?.activeWhen || item.activeWhen.length === 0) {
    return false;
  }
  const path = stripQueryAndFragment(url);
  return item.activeWhen.some((prefix) => matchesPathPrefix(path, prefix));
}
